import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import api from '../utilities/api';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';

const ApiHealthModal = ({ open, onClose, initialAdding = false }) => {
    const theme = useTheme();
    const [urls, setUrls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statuses, setStatuses] = useState({}); 
    
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', url: '' });
    const [isAdding, setIsAdding] = useState(initialAdding);

    useEffect(() => {
        if (open) {
            fetchUrls();
            if (initialAdding) {
                setIsAdding(true);
            }
        }
    }, [open, initialAdding]);

    const fetchUrls = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/health-check');
            setUrls(res.data.data);
            res.data.data.forEach(item => checkHealth(item._id, item.url));
        } catch (error) {
            console.error("Failed to fetch URLs", error);
        } finally {
            setLoading(false);
        }
    };

    const checkHealth = async (id, url) => {
        setStatuses(prev => ({ ...prev, [id]: 'Checking' }));
        try {
            await axios.get(url, { timeout: 5000 });
            setStatuses(prev => ({ ...prev, [id]: 'OK' }));
        } catch (error) {
            console.error(`Health check failed for ${url}`, error);
            setStatuses(prev => ({ ...prev, [id]: 'Error' }));
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.url) return;
        
        try {
            if (editingId) {
                await api.put(`/api/health-check/${editingId}`, formData);
            } else {
                await api.post('/api/health-check', formData);
            }
            fetchUrls();
            resetForm();
        } catch (error) {
            console.error("Failed to save", error);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/api/health-check/${id}`);
            fetchUrls();
        } catch (error) {
            console.error("Failed to delete", error);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ name: '', url: '' });
        setIsAdding(false);
    };

    const startEdit = (item) => {
        setEditingId(item._id);
        setFormData({ name: item.name, url: item.url });
        setIsAdding(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Apps Backends Health Monitor</Typography>
                <Button 
                    startIcon={<AddIcon />} 
                    variant="contained" 
                    size="small" 
                    onClick={() => { setIsAdding(true); setEditingId(null); setFormData({ name: '', url: '' }); }}
                    disabled={isAdding || editingId}
                >
                    Add API
                </Button>
            </DialogTitle>
            <DialogContent dividers>
                {/* Add/Edit Form */}
                {(isAdding || editingId) && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {editingId ? 'Edit API Link' : 'Add New API Link'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label="Name"
                                size="small"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="URL"
                                size="small"
                                value={formData.url}
                                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                sx={{ flex: 2 }}
                            />
                            <IconButton color="primary" onClick={handleSave} disabled={!formData.name || !formData.url}>
                                <SaveIcon />
                            </IconButton>
                            <IconButton color="error" onClick={resetForm}>
                                <CancelIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}

                {/* List */}
                {loading && urls.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List>
                        {urls.map((item) => (
                            <ListItem
                                key={item._id}
                                sx={{
                                    bgcolor: theme.palette.background.paper,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`
                                }}
                                secondaryAction={
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton size="small" onClick={() => checkHealth(item._id, item.url)}>
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => startEdit(item)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(item._id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                    {/* Status Indicator */}
                                    <Box sx={{ width: 100, display: 'flex', alignItems: 'center' }}>
                                        {statuses[item._id] === 'Checking' ? (
                                            <CircularProgress size={20} />
                                        ) : statuses[item._id] === 'OK' ? (
                                            <Chip 
                                                icon={<CheckCircleIcon style={{ color: 'white' }} />} 
                                                label="OK" 
                                                size="small" 
                                                color="success" 
                                                sx={{ width: '100%' }}
                                            />
                                        ) : statuses[item._id] === 'Error' ? (
                                            <Chip 
                                                icon={<ErrorIcon style={{ color: 'white' }} />} 
                                                label="Error" 
                                                size="small" 
                                                color="error" 
                                                sx={{ width: '100%' }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">Unknown</Typography>
                                        )}
                                    </Box>
                                    
                                    <ListItemText
                                        primary={item.name}
                                        secondary={item.url}
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                            </ListItem>
                        ))}
                        {urls.length === 0 && !loading && (
                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                No API links found. Add one to get started.
                            </Typography>
                        )}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApiHealthModal;
